import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { reportRuntimeError } from "@/lib/diagnostics.functions";
import { supabase } from "@/integrations/supabase/client";
import { getBuildId, getCorrelationId, getReleaseChannel } from "@/lib/correlation";
import { isRole, type AccountRole } from "@/lib/role-access";

/** Ruolo attivo memorizzato localmente, se disponibile (best effort). */
function activeRole(): AccountRole | undefined {
  try {
    for (const key of Object.keys(window.localStorage)) {
      if (!key.toLowerCase().includes("role")) continue;
      const raw = window.localStorage.getItem(key);
      if (isRole(raw)) return raw;
      try {
        const parsed = JSON.parse(raw ?? "");
        if (isRole(parsed)) return parsed;
        if (parsed && typeof parsed === "object" && isRole(parsed.activeRole)) return parsed.activeRole;
      } catch {
        // valore non JSON: si ignora
      }
    }
  } catch {
    // storage non disponibile
  }
  return undefined;
}

/**
 * Cattura gli errori non gestiti del client (eccezioni e promise rifiutate)
 * e li invia al registro interno con il correlation ID di sessione, così
 * l'errore si segue dal client fino alla build e alla source map corrispondente.
 */
export function RuntimeErrorReporter() {
  const report = useServerFn(reportRuntimeError);

  useEffect(() => {
    const seen = new Set<string>();
    const correlationId = getCorrelationId();
    const buildId = getBuildId();
    const releaseChannel = getReleaseChannel();

    const send = async (
      message: string,
      stack: string | undefined,
      source: string | undefined,
      severity: "error" | "fatal",
    ) => {
      const key = `${message}|${source ?? ""}`;
      if (seen.has(key)) return; // niente spam: un errore identico si registra una volta a sessione
      seen.add(key);
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session) return; // il registro accetta solo utenti autenticati
        await report({
          data: {
            message: message.slice(0, 2000),
            stack: stack?.slice(0, 20000),
            source,
            severity,
            correlationId,
            buildId,
            releaseChannel,
            actorRole: activeRole(),
            context: {
              url: window.location.href,
              userAgent: navigator.userAgent,
              at: new Date().toISOString(),
              correlationId,
              buildId,
            },
          },
        });
      } catch {
        // La segnalazione non deve mai peggiorare l'errore originale.
      }
    };

    const onError = (e: ErrorEvent) => {
      void send(
        e.message || "Errore JavaScript",
        e.error instanceof Error ? e.error.stack : undefined,
        e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined,
        "error",
      );
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      void send(
        reason instanceof Error ? reason.message : String(reason ?? "Promise rifiutata"),
        reason instanceof Error ? reason.stack : undefined,
        "unhandledrejection",
        "error",
      );
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, [report]);

  return null;
}
